/**
 * Accessible hourly drop target carrying dateKey, hour, and slotHeight to dnd-kit. Parent-supplied
 * children occupy the slot; work-window boundaries and out-of-hours shading are presentation only.
 *
 * @module apps/web/src/components/calendar/DroppableTimeSlot
 */

import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '@/lib/utils';

/** Stable local date/hour drop identity, pixel geometry, and work-window decorations for a timeline cell. */
interface DroppableTimeSlotProps {
  /** Unique dnd-kit drop-target identifier. */
  id: string;
  /** Local civil date in YYYY-MM-DD form. */
  dateKey: string;
  /** Local hour represented by this grid cell. */
  hour: number;
  /** Pixel height of one hour at the current zoom, carried in the drop data. The cell's own
   * height comes from its grid row, which the owning timeline sizes. */
  slotHeight: number;
  /** Timeline blocks rendered inside the slot. */
  children: React.ReactNode;
  /** Minute offsets within this hour at which work-window lines are drawn. */
  workingBoundaryMinutes?: number[];
  /** Shade the slot outside the configured work window. */
  isOutsideWorkingHours?: boolean;
}

/**
 * Accessible hourly drop target carrying dateKey, hour, and slotHeight to dnd-kit. Parent-supplied
 * children occupy the slot; work-window boundaries and out-of-hours shading are presentation only.
 */
export function DroppableTimeSlot({
  id,
  dateKey,
  hour,
  slotHeight,
  children,
  workingBoundaryMinutes = [],
  isOutsideWorkingHours = false,
}: DroppableTimeSlotProps) {
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: { type: 'TimeSlot', dateKey, hour, slotHeight },
  });

  return (
    <div
      ref={setNodeRef}
      role="gridcell"
      aria-label={`Time slot at ${hour}:00 on ${dateKey}`}
      className={cn(
        'calendar-slot relative border-r border-t',
        isOutsideWorkingHours && 'bg-muted/20',
        isOver && 'bg-primary/10 ring-primary/50 ring-2'
      )}
    >
      {workingBoundaryMinutes.map(minute => (
        <div
          key={minute}
          aria-hidden="true"
          className="pointer-events-none absolute left-0 right-0 z-10 border-t-2 border-foreground/80"
          style={{ top: `${(minute / 60) * 100}%` }}
        />
      ))}
      {children}
    </div>
  );
}
